import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Phone, MessageCircle, ShieldCheck } from "lucide-react";
import { CORE_PILLARS, SERVICES, CAPABILITIES, TRUST_MARQUEE, COMPANY } from "@/data/site";
import { PRODUCT_CARDS } from "@/data/landing";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";
import Seo from "@/components/Seo";
import Testimonials from "@/components/Testimonials";
import BrochureCta from "@/components/BrochureCta";
import FeaturedProducts from "@/components/FeaturedProducts";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import AppDownloadSection from "@/components/AppDownloadSection";
import TrustBar from "@/components/TrustBar";
import WhyChoose from "@/components/WhyChoose";
import VoltechLive from "@/components/VoltechLive";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo path="/" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-noise opacity-50 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, rgba(230,57,70,0.18), transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12 md:pb-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="eyebrow flex items-center gap-3" data-testid="hero-eyebrow">
                <span className="inline-block w-2 h-2 bg-[#e63946] rounded-full vps-pulse" />
                Made in Hyderabad · Pan-India delivery
              </div>
              <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.92] tracking-tighter mt-6">
                Power the next
                <br />
                <span className="text-[#e63946]">decade</span> with
                <br />
                lithium &amp; sunlight.
              </h1>
              <p className="mt-7 text-zinc-400 text-base md:text-lg max-w-xl leading-relaxed">
                VOLTECH POWER SOLUTIONS is a Hyderabad-based manufacturer of
                <span className="text-white"> lithium-ion batteries</span>,
                <span className="text-white"> solar street lights</span>,
                <span className="text-white"> electric vehicles</span> and
                turnkey
                <span className="text-white"> CSR energy projects</span> —
                built end-to-end, in-house.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(WA_MESSAGES.generic)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="hero-quote-btn"
                >
                  <MessageCircle size={16} /> Get a Free Quote on WhatsApp
                </a>
                <Link to="/products" className="btn-secondary" data-testid="hero-browse-btn">
                  Browse Products <ArrowUpRight size={16} />
                </Link>
              </div>

              <div className="mt-5 flex items-center gap-4 flex-wrap">
                <GooglePlayBadge testId="hero-google-play" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 max-w-[180px]">
                  Book battery & EV service from our app
                </span>
              </div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
                {[
                  { l: "End-to-end", v: "In-house manufacturing" },
                  { l: "Custom", v: "Battery pack design" },
                  { l: "Pan-India", v: "Delivery & service" },
                  { l: "Trusted", v: "B2B partner" },
                ].map((s) => (
                  <div key={s.l} className="border-l-2 border-[#e63946] pl-3" data-testid={`hero-stat-${s.l.toLowerCase()}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {s.l}
                    </div>
                    <div className="font-display text-sm md:text-base mt-1 leading-tight">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero image collage */}
            <div className="lg:col-span-5 grid grid-cols-6 gap-3 lg:gap-4">
              <div className="col-span-4 row-span-2 industrial-card overflow-hidden">
                <img
                  src={CORE_PILLARS[0].image}
                  alt="Lithium battery"
                  className="w-full h-full object-cover aspect-[3/4]"
                />
              </div>
              <div className="col-span-2 industrial-card overflow-hidden">
                <img
                  src={CORE_PILLARS[1].image}
                  alt="Solar"
                  className="w-full h-full object-cover aspect-square"
                />
              </div>
              <div className="col-span-2 industrial-card bg-[#e63946] p-4 flex flex-col justify-between min-h-[120px]">
                <Zap size={20} className="text-white" />
                <div className="font-display text-white text-sm uppercase leading-tight">
                  4 Core
                  <br />
                  Pillars
                </div>
              </div>
              <div className="col-span-3 industrial-card overflow-hidden">
                <img
                  src={CORE_PILLARS[2].image}
                  alt="EV"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
              </div>
              <div className="col-span-3 industrial-card overflow-hidden">
                <img
                  src={CORE_PILLARS[3].image}
                  alt="CSR"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-t border-white/10 bg-black py-3 overflow-hidden">
          <div className="vps-marquee text-[#e63946] font-mono text-xs uppercase tracking-[0.3em]">
            {[...TRUST_MARQUEE, ...TRUST_MARQUEE].map((t, i) => (
              <span key={i} className="flex items-center gap-12">
                <span>{t}</span>
                <span className="text-zinc-700">+</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <TrustBar />

      {/* PRODUCT CARDS */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="section-product-cards">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow">What we make</div>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
                Explore our range.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCT_CARDS.map((c, idx) => (
              <Link
                to={c.to}
                key={idx}
                className="industrial-card group p-7 flex flex-col"
                data-testid={`home-product-card-${idx}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  0{idx + 1}
                </div>
                <h3 className="font-display text-xl uppercase mt-3 leading-tight">
                  {c.name}
                </h3>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed flex-1">
                  {c.description}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-display font-bold uppercase tracking-wider text-[#e63946] group-hover:gap-3 transition-all">
                  {c.button} <ArrowUpRight size={15} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CORE PILLARS */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="section-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow">04 — Core focus</div>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
                Our four pillars.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm md:text-base">
              The verticals we lead with — backed by 15+ adjacent services
              across batteries, solar, EV and community projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CORE_PILLARS.map((p, idx) => (
              <Link
                to={`/services#${p.id}`}
                key={p.id}
                className="industrial-card group flex flex-col"
                data-testid={`pillar-card-${p.id}`}
              >
                <div className="aspect-[4/5] overflow-hidden border-b border-white/10">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">
                        0{idx + 1} · {p.short}
                      </div>
                      <h3 className="font-display text-xl uppercase mt-2 leading-tight">
                        {p.title}
                      </h3>
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="text-zinc-500 group-hover:text-[#e63946] transition-colors flex-shrink-0"
                    />
                  </div>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed flex-1">
                    {p.description}
                  </p>
                  <div className="mt-4 tag-chip-red tag-chip">{p.spec}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <Testimonials />

      <FeaturedProducts />

      <WhyChoose />

      <VoltechLive />

      <section className="py-20 md:py-28 border-b border-white/10 bg-[#0a0a0c]" data-testid="section-capabilities">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow">What we do</div>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
                Engineered, <span className="text-[#e63946]">in-house.</span>
              </h2>
              <p className="text-zinc-400 mt-5 text-sm leading-relaxed max-w-sm">
                From cell sourcing to pack-level QC, from pole fabrication to
                installation — every link of the value chain runs out of our
                Cherlapally facility.
              </p>
              <div className="mt-8 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">
                <ShieldCheck size={14} className="text-[#e63946]" />
                GSTIN · {COMPANY.gstin}
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CAPABILITIES.map((c) => (
                <div key={c.code} className="industrial-card p-6" data-testid={`capability-${c.code}`}>
                  <div className="font-mono text-xs text-[#e63946] tracking-[0.2em]">
                    {c.code}
                  </div>
                  <div className="font-display text-lg uppercase mt-3 leading-tight">
                    {c.title}
                  </div>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ALL SERVICES STRIP */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="section-services-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow">Full catalogue</div>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
                Everything we deliver.
              </h2>
            </div>
            <Link to="/services" className="btn-secondary" data-testid="services-strip-cta">
              View all services <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {SERVICES.map((s, idx) => (
              <div
                key={s.name}
                className="industrial-card p-4 flex items-center justify-between group"
                data-testid={`service-chip-${idx}`}
              >
                <div>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">
                    {s.category}
                  </div>
                  <div className="text-sm font-medium mt-1">{s.name}</div>
                </div>
                <ArrowUpRight size={14} className="text-zinc-600 group-hover:text-[#e63946]" />
              </div>
            ))}
          </div>

          <div className="mt-10">
            <BrochureCta testId="home-brochure-cta" />
          </div>
        </div>
      </section>

      <AppDownloadSection />

      {/* CTA */}
      <section className="py-20 md:py-28" data-testid="section-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="industrial-card p-10 md:p-16 relative overflow-hidden">
            <div
              className="absolute -top-20 -right-20 w-72 h-72 opacity-20"
              style={{
                background:
                  "radial-gradient(circle, #e63946 0%, transparent 70%)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="eyebrow">Let's build</div>
                <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter leading-[0.95]">
                  Need a battery, light or EV
                  <br />
                  built to <span className="text-[#e63946]">your spec?</span>
                </h2>
                <p className="text-zinc-400 mt-4 max-w-xl text-sm md:text-base">
                  Share your requirement and our engineering team will revert
                  within 24 working hours with a sizing recommendation &
                  quote.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link to="/contact" className="btn-primary justify-center" data-testid="cta-quote-btn">
                  Get a Quote <ArrowUpRight size={16} />
                </Link>
                <a
                  href={`tel:${COMPANY.phoneSales}`}
                  className="btn-secondary justify-center"
                  data-testid="cta-call-btn"
                >
                  <Phone size={16} /> Call {COMPANY.phoneSales}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
